import { payrollApi } from 'Api/payroll';
import { Col, Row, Table, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatNumber, toastText } from 'utils/utils';
import GlobalHeader from '../../GlobalHeader';
import styles from './index.module.scss';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Column } = Table;

type Props = {
	selectedPayrollId: string;
	setIsUniversalLoading: any;
	headerData: any;
	setRemainingCount: any;
	setInvalidCount: any;
	showSteps: boolean;
};
const AdjustedBasicPayTable = (props: Props) => {
	const {
		selectedPayrollId,
		setIsUniversalLoading,
		headerData,
		setRemainingCount,
		setInvalidCount,
		showSteps,
	} = props;
	const [adjustedNetPayData, setAdjustedNetPayData] = useState<any>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [page, setPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageSize, setPageSize] = useState(31);

	const tableChangeHandler = (pagination: any, filters: any, sorter: any) => {
		setTotalRecords(pagination.total);
		setPage(pagination.current);
		setPageSize(pagination.pageSize);
	};

	const { data } = useSelector((state: any) => state?.userProfile);

	const { conversionRate, usdCurrencyToggle, isUsdCurrency } = useSelector(
		(state: any) => state.usdCurrencyToggle
	);

	const FormattedValue = (value: any) => {
		if (!usdCurrencyToggle && isUsdCurrency) {
			return formatNumber(Number(value), data?.CompanyCurrencies, true);
		} else {
			return formatNumber(
				Number(value * (conversionRate ?? 1)),
				data?.CompanyCurrencies
			);
		}
	};

	const fetchAdjustedNetPay = async (payrollId: string) => {
		try {
			setIsLoading(true);
			setIsUniversalLoading(true);
			const { data } = await payrollApi.getAdjustedNetPay({
				payrollId: payrollId,
				page: page,
				pageSize: pageSize - 1,
			});
			const { employees } = data.data;
			const sortedEmployees = employees.sort((a: any, b: any) => {
				if (a.bspAdjustment < 0 && b.bspAdjustment >= 0) return -1;
				if (a.bspAdjustment >= 0 && b.bspAdjustment < 0) return 1;
				return 0;
			});

			setAdjustedNetPayData(sortedEmployees);

			//push total to the end of the array
			setTotalRecords(data.data.totalRecords);

			setRemainingCount(data.data.remainingEmployees);

			setInvalidCount(data.data.invalidEarnings);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching Adjusted Net Pay';
			toastText(errorMessage, 'error');
		} finally {
			setIsLoading(false);
			setIsUniversalLoading(false);
		}
	};

	useEffect(() => {
		fetchAdjustedNetPay(selectedPayrollId);
	}, [selectedPayrollId, page, pageSize]);

	return (
		<>
			<div className="payroll-steps-table">
				<GlobalHeader
					headerData={headerData}
					toggleRequired={true}
					fetchAllData={() => fetchAdjustedNetPay(selectedPayrollId)}
				/>
				<Row>
					<Col span={24}>
						<Table
							dataSource={adjustedNetPayData}
							scroll={{
								y: showSteps
									? 'calc(90vh - 390px)'
									: 'calc(90vh - 269px)',
							}}
							pagination={{
								total: totalRecords,
								current: page,
								pageSize: pageSize,
								showSizeChanger: false,
								pageSizeOptions: [10, 20, 50, 100, 200],
							}}
							rowKey={(record) => record.id}
							loading={isLoading}
							onChange={tableChangeHandler}
						>
							<Column
								title="Employee Name | Code"
								dataIndex="employeeCode"
								key="employeeCode"
								// sorter={true}
								width={'40%'}
								className="bg-white"
								render={(text, record: any) => (
									<>
										{record.field !== 'Total' ? (
											<>
												{' '}
												<span>
													{record.employeeName}
												</span>{' '}
												|{' '}
												<span className="color-purple">
													{record.employeeId}
												</span>
											</>
										) : (
											<p className={styles['total']}>
												{record.firstName}
											</p>
										)}
										{record.bspAdjustment < 0 && (
											<Tooltip title="Basic pay of this employee can't be adjusted ,Please decrease the earnings or increase the net pay.">
												<InfoCircleOutlined
													style={{
														marginLeft: 4,
														fontSize: '14px',
														color: '#584495',
													}}
												/>
											</Tooltip>
										)}
									</>
								)}
							/>

							<Column
								title="Total Earnings"
								dataIndex="totalEarnings"
								key="totalEarnings"
								className="bg-white"
								width={'20%'}
								// sorter={true}
								render={(value: any, record: any) => {
									if (record.field === 'Total') {
										return (
											<p className={styles['total']}>
												{FormattedValue(value)}
											</p>
										);
									} else {
										return FormattedValue(value);
									}
								}}
							/>
							<Column
								title="Total Deductions"
								key="totalDeductions"
								dataIndex="totalDeductions"
								className="bg-white"
								width={'20%'}
								render={(value: any, record: any) => {
									if (record.field === 'Total') {
										return (
											<p className={styles['total']}>
												{FormattedValue(value)}
											</p>
										);
									} else {
										return FormattedValue(value);
									}
								}}
							/>
							<Column
								title="Calculated Net Pay"
								dataIndex="totalNetPay"
								key="totalNetPay"
								className="bg-white"
								width={'20%'}
								// sorter={true}
								render={(value: string, record: any) => {
									if (record.field === 'Total') {
										return (
											<p className={styles['total']}>
												{FormattedValue(value)}
											</p>
										);
									}
									return FormattedValue(value);
								}}
							/>
							<Column
								title="Gross Net Pay"
								dataIndex="baseNetPay"
								key="baseNetPay"
								className="bg-white"
								width={'20%'}
								render={(value: string, record: any) => {
									if (record.field === 'Total') {
										return (
											<p className={styles['total']}>
												{FormattedValue(value)}
											</p>
										);
									}
									return FormattedValue(value);
								}}
							/>
							<Column
								title="Adjusted Basic Pay"
								dataIndex="bspAdjustment"
								key="bspAdjustment"
								className="bg-white"
								width={'20%'}
								render={(value: string, record: any) => {
									if (record.field === 'Total') {
										return (
											<p className={styles['total']}>
												{FormattedValue(value)}
											</p>
										);
									}
									return FormattedValue(value);
								}}
							/>
						</Table>
					</Col>
				</Row>
			</div>
		</>
	);
};
export default AdjustedBasicPayTable;
